import { useContext, useEffect, useMemo, useState } from "react";
import GIcon from "../GIcon";
import { Avatar, AvatarGroup, Fade, Stack, Tooltip } from "@mui/material";
import IconFilterBar from "./iconFilterBar"
import { Context } from "../ChatPanel/ChatContext";
import { IUser, ITopic, TypingEvent, TopicTypes } from "../../types"
import { StyledBadge } from "../styledBadge"
import { useSocket } from "../../atoms/socket";
import { useAtomInstance, useAtomSelector, useAtomValue } from "@zedux/react";
import { onlineUsersAtom } from '../../atoms/onlineUsersAtom';
import { firstLetter } from "../../functions/firstLetter";
import { activeTopicAtom } from "../../atoms/atoms";
import { userByIdSelector } from "../../atoms/userListAtom";
import agoCalculator from "../../functions/agoCalculator";
import cx from "classix";
import { farsi_number } from "../../functions/farsi";
import ArrowIcon2 from "@src/icons/ArrowIcon2";
import DotsIcon from "@src/icons/DotsIcon";

export default function TopicHeader({ topic }: { topic: ITopic }) {
    const socket = useSocket()

    const { other_display, other_user_id, topic_name, topic_type, avatar_url, admin_id, participant_count, participant_user_ids } = topic

    const onlineUsers = useAtomValue(onlineUsersAtom)
    const isGroup = topic_type === TopicTypes.GROUP || topic_type === TopicTypes.CHANNEL

    const [isOtherOnline, onlineUsersInGroup] = useMemo(() => {
        const is_other_online = other_user_id ? onlineUsers.has(other_user_id) : false
        let group_online_count: number | null = null
        if (isGroup) {
            group_online_count = participant_user_ids ? participant_user_ids.filter(id => onlineUsers.has(id)).length : 0
        }
        return [is_other_online, group_online_count]
    }, [participant_user_ids, topic_type, onlineUsers, other_user_id])

    const { context, userInfo, other, closePopup, helpMode = false } = useContext(Context)
    const setActiveTopicIDInstance = useAtomInstance(activeTopicAtom, [context])

    // Prefer store data; fall back to topic fields
    const storedUser = useAtomSelector(userByIdSelector, other_user_id as number)
    const displayName = storedUser?.fullname || other_display || topic_name
    const displayAvatar = storedUser?.avatar_url || avatar_url

    const lastSeenText = useMemo(() => {
        // For group/channel topics there's no meaningful "last seen"
        if (topic_type === TopicTypes.CHANNEL || topic_type === TopicTypes.GROUP) return '';
        // Prefer the fresh value from the store, fall back to the topic field
        return agoCalculator(storedUser?.last_seen ?? null);

    }, [storedUser?.last_seen, topic_type]);

    const [isTyping, setIsTyping] = useState("")

    const handleTyping = (data: TypingEvent) => {
        if (topic.topic_id != data.topicId) {
            return
        }
        setIsTyping(data.isTyping ? data.event : '')
    }
    useEffect(() => {
        if (socket) {
            socket.on("typing", handleTyping)
        }
        return () => { socket?.off("typing", handleTyping) }
    }, [topic])

    const handleBack = () => {
        if (other != null) {
            if (closePopup) {
                closePopup()
            }
        } else {
            setActiveTopicIDInstance.exports.closeCurrentTopic()
        }
    }

    return <div onContextMenu={(e) => e.persist()} className={cx('flex flex-row justify-between items-center border-b border-b-neutral-40 w-full h-16', helpMode && 'bg-primary-00')}>
        <div className="flex flex-row items-center max-lg:grow">
            <div onClick={handleBack} className="px-2">
                <ArrowIcon2 className={'size-5 cursor-pointer'} />
            </div>

            <div className='relative flex lg:flex-row flex-row-reverse items-center gap-2 lg:gap-4 max-lg:grow'>
                <StyledBadge
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    invisible={isGroup || !isOtherOnline}
                    color="success" variant="dot" overlap="circular"
                >
                    {
                        topic_type == TopicTypes.CHANNEL || topic_type == TopicTypes.GROUP ?
                            <Avatar className="rounded-full" src={displayAvatar ?? ''}>
                                {firstLetter(topic_name)}
                            </Avatar> :
                            <Avatar className="rounded-full" src={displayAvatar ?? ''}>
                                {firstLetter(displayName)}
                            </Avatar>
                    }

                </StyledBadge>
                <div className="flex flex-col justify-center items-end lg:items-start">
                    <p className="m-0 font-bold text-sm lg:text-lg line-clamp-1">{topic_name.length > 0 ? topic_name : displayName}</p>
                    {!isGroup && lastSeenText !== false &&
                        <p className={cx('m-0 text-sm ', isOtherOnline ? "text-sky-500" : "text-stone-400")}>
                            {isTyping.length != 0 ? isTyping : (isOtherOnline) ? "آنلاین" : lastSeenText}
                        </p>
                    }
                    {isGroup &&
                        <p className={cx('m-0 text-sm ')}>
                            {farsi_number(participant_count ?? 0)} عضو، {farsi_number(onlineUsersInGroup ?? 0)} آنلاین
                        </p>
                    }
                </div>
            </div>
        </div>
        {!helpMode && <div className='flex justify-center items-center content-center font-semibold'>
            <IconFilterBar topic={topic} is_admin={admin_id === userInfo.userId} context={context} />
        </div>}
    </div>
}
